import React, { useMemo } from 'react'
import { ScissorOutlined } from '@ant-design/icons'
import { styled } from 'styled-components'
import { CustomReportSectionsType } from '../../../../../redux/context/customReports/typesSection'
import { getTitleByLanguage } from '../customReportTable/utils'
import { ContentGrid, GridSection } from './StyledPaper'
import PrintSection from './PrintSection'
import { buildSectionWidthMap } from './paperConfig'

const FORCE_COLOR = '#1677ff'

const ForceBreakToggle = styled.div<{ $active: boolean; $counterZoom: number }>`
  position: absolute;
  top: -5mm;
  left: 0;
  right: 0;
  height: 5mm;
  z-index: 15;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`

const ForceBreakLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-top: 2px solid ${FORCE_COLOR};
  opacity: 0.6;
`

const ForceBreakIcon = styled.span<{ $active: boolean; $counterZoom: number }>`
  position: relative;
  font-size: 12px;
  color: ${({ $active }) => ($active ? FORCE_COLOR : '#8c8c8c')};
  background: #fff;
  padding: 0 6px;
  border-radius: 4px;
  border: 1px solid ${({ $active }) => ($active ? FORCE_COLOR : '#d9d9d9')};
  line-height: 18px;
  transform: scale(${({ $counterZoom }) => $counterZoom});
`

interface PrintReportContentProps {
  evaluatedSectionMap: Map<number, CustomReportSectionsType | null>
  sectionMap: Map<string, CustomReportSectionsType>
  heightMap: Map<string, number>
  colSpanMap: Map<string, number>
  areas: string
  rows: string
  printableWidthPx: number
  forcedBreaks?: Set<number>
  onToggleForcedBreak?: (sectionId: number) => void
  counterZoom?: number
}

const PrintReportContent: React.FC<PrintReportContentProps> = ({
  evaluatedSectionMap,
  sectionMap,
  heightMap,
  colSpanMap,
  areas,
  rows,
  printableWidthPx,
  forcedBreaks,
  onToggleForcedBreak,
  counterZoom = 1
}) => {
  const sectionWidthMap = useMemo(
    () => buildSectionWidthMap(printableWidthPx, colSpanMap),
    [printableWidthPx, colSpanMap]
  )

  return (
    <ContentGrid $areas={areas} $rows={rows}>
      {[...sectionMap.entries()].map(([name, configSection]) => {
        const evaluated = evaluatedSectionMap.get(configSection.id) ?? null
        const active = forcedBreaks?.has(configSection.id) ?? false
        return (
          <GridSection
            key={configSection.id}
            data-section-id={configSection.id}
            data-section-type={configSection.type}
            data-area={name}
            style={{ gridArea: name }}
          >
            {onToggleForcedBreak && (
              <ForceBreakToggle
                data-print-ignore
                role="button"
                tabIndex={0}
                $active={active}
                $counterZoom={counterZoom}
                onClick={() => onToggleForcedBreak(configSection.id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') onToggleForcedBreak(configSection.id)
                }}
              >
                {active && <ForceBreakLine />}
                <ForceBreakIcon $active={active} $counterZoom={counterZoom}>
                  <ScissorOutlined />
                </ForceBreakIcon>
              </ForceBreakToggle>
            )}
            <PrintSection
              section={evaluated}
              sectionName={getTitleByLanguage(configSection)}
              fixedHeight={heightMap.get(name)}
              sectionWidthPx={sectionWidthMap.get(name)}
              printableWidthPx={printableWidthPx}
            />
          </GridSection>
        )
      })}
    </ContentGrid>
  )
}

export default React.memo(PrintReportContent)
